import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import Header from "@/components/Header";
import MonthCell from "@/components/MonthCell";
import {
  Filter,
  AlertTriangle,
  Upload,
  X,
  Download,
  FileSpreadsheet,
  Layers,
  ExternalLink,
  CheckSquare,
  Check,
  CircleSlash,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const MESI_SHORT = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic",
];
const MESI_FULL = [
  "", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [anno, setAnno] = useState(2026);
  const [filtroCons, setFiltroCons] = useState("");
  const [toast, setToast] = useState(null);
  // pending optimistic updates per cell (codice + mese)
  const [busyCells, setBusyCells] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/setup/status").then((r) => {
      if (r.data.needs_setup) {
        nav("/setup", { replace: true });
      }
    });
    const t = sessionStorage.getItem("pa_toast");
    if (t) {
      try {
        setToast(JSON.parse(t));
        sessionStorage.removeItem("pa_toast");
        setTimeout(() => setToast(null), 5000);
      } catch (e) { /* ignore */ }
    }
  }, [nav]);

  const load = () => {
    setLoading(true);
    const params = { anno };
    if (filtroCons) params.consulente_id = filtroCons;
    api
      .get("/dashboard", { params })
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || "Errore di caricamento"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anno, filtroCons]);

  const anniOpzioni = useMemo(() => {
    const y = new Date().getFullYear();
    const list = new Set([2026, y]);
    for (let i = y - 3; i <= y + 1; i++) list.add(i);
    return Array.from(list).sort((a, b) => b - a);
  }, []);

  // Click on a month cell: ALWAYS navigate to detail page.
  // Right-click opens the context menu (mark as acquired, etc.)
  const handleCellClick = (row, mese, cell) => {
    nav(`/cliente/${row.codice}/${anno}/${mese}`);
  };

  // Toggle the "acquisito esterno" flag (used by the context-menu items).
  const toggleAcquisitoEsterno = async (row, mese, flagged) => {
    const key = `${row.codice}-${mese}`;
    setBusyCells((prev) => new Set(prev).add(key));
    try {
      await api.post(
        `/acquisizioni/by-cliente/${row.codice}/${anno}/${mese}/mark-external`,
        { flagged },
      );
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Operazione non riuscita.");
    } finally {
      setBusyCells((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div data-testid="dashboard-page">
      <Header />
      {toast && (
        <div
          data-testid="toast"
          style={{
            position: "fixed",
            top: 78,
            right: 24,
            background: toast.type === "ok" ? "var(--pa-green-bg)" : "var(--pa-red-bg)",
            color: toast.type === "ok" ? "var(--pa-green)" : "var(--pa-red)",
            border: `1px solid ${toast.type === "ok" ? "#CDE5BA" : "#EFC7C7"}`,
            padding: "10px 14px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            zIndex: 40,
            boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {toast.text}
          <button
            onClick={() => setToast(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 2,
              display: "inline-flex",
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="pa-container" style={{ padding: "32px 24px 64px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 22,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "var(--pa-navy)",
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              Dashboard acquisizioni
            </h1>
            <p
              style={{
                color: "var(--pa-muted)",
                fontSize: 14,
                marginTop: 4,
                maxWidth: 760,
              }}
            >
              <strong style={{ color: "var(--pa-green)" }}>Verde</strong> = mese già acquisito ·{" "}
              <strong style={{ color: "var(--pa-amber)" }}>giallo</strong> = PDF caricato da elaborare ·{" "}
              <strong style={{ color: "var(--pa-muted)" }}>grigio</strong> = niente.
              <br />
              <strong>Clic</strong> su un mese per aprire il dettaglio · <strong>Tasto destro</strong> per le opzioni rapide (es. segna come già acquisito).
            </p>
            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => nav("/import-blocco")}
                data-testid="open-import-blocco"
                className="pa-btn-primary"
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 13,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Upload size={14} /> Import in blocco
              </button>
              <button
                onClick={() => setBulkDialogOpen(true)}
                data-testid="open-bulk-mark"
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 13,
                  background: "transparent",
                  border: "1px solid var(--pa-border)",
                  color: "var(--pa-navy)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CheckSquare size={14} /> Marca periodi
              </button>
            </div>
          </div>

          {data && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div className="pa-stat" data-testid="stat-da-generare">
                <span className="pa-stat-label">Da generare</span>
                <span className="pa-stat-value" style={{ color: "var(--pa-amber)" }}>
                  {data.counts.excel_da_generare}
                </span>
              </div>
              <div className="pa-stat" data-testid="stat-pronti">
                <span className="pa-stat-label">Excel pronti</span>
                <span className="pa-stat-value" style={{ color: "var(--pa-green)" }}>
                  {data.counts.excel_pronti}
                </span>
              </div>
              <div className="pa-stat" data-testid="stat-esterno">
                <span className="pa-stat-label">Già acquisiti ✓</span>
                <span className="pa-stat-value" style={{ color: "var(--pa-green)" }}>
                  {data.counts.acquisito_esterno || 0}
                </span>
              </div>
              <div className="pa-stat" data-testid="stat-senza-pdf">
                <span className="pa-stat-label">Senza PDF</span>
                <span className="pa-stat-value" style={{ color: "var(--pa-muted)" }}>
                  {data.counts.mesi_senza_pdf}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Filtri */}
        <div
          className="pa-card"
          style={{
            padding: "14px 18px",
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "var(--pa-muted)",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <Filter size={14} /> Filtri
          </div>
          <Select
            label="Anno"
            value={anno}
            onChange={(v) => setAnno(Number(v))}
            options={anniOpzioni.map((y) => ({ value: y, label: y }))}
            testid="filtro-anno"
          />
          <Select
            label="Consulente"
            value={filtroCons}
            onChange={setFiltroCons}
            options={[
              { value: "", label: "Tutti" },
              ...(data?.consulenti || []).map((c) => ({
                value: c.id,
                label: c.nome,
              })),
            ]}
            testid="filtro-consulente"
          />
        </div>

        {error && (
          <div
            style={{
              background: "var(--pa-red-bg)",
              color: "var(--pa-red)",
              padding: 12,
              borderRadius: 8,
              marginBottom: 12,
            }}
            data-testid="dashboard-error"
          >
            {error}
          </div>
        )}

        {/* Tabella */}
        <div
          className="pa-card"
          style={{ overflow: "auto" }}
          data-testid="dashboard-table-wrap"
        >
          <table className="pa-table" data-testid="dashboard-table">
            <thead>
              <tr>
                <th style={{ minWidth: 220, paddingLeft: 18 }}>Cliente</th>
                <th style={{ minWidth: 170 }}>Consulente</th>
                {MESI_SHORT.map((m) => (
                  <th
                    key={m}
                    style={{ textAlign: "center", paddingLeft: 6, paddingRight: 6 }}
                  >
                    {m}
                  </th>
                ))}
                <th style={{ minWidth: 130, textAlign: "right", paddingRight: 18 }}>
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={15} style={{ padding: 40, textAlign: "center", color: "var(--pa-muted)" }}>
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading && data?.rows.length === 0 && (
                <tr>
                  <td colSpan={15} style={{ padding: 40, textAlign: "center", color: "var(--pa-muted)" }}>
                    Nessun cliente.
                  </td>
                </tr>
              )}
              {!loading &&
                data?.rows.map((r) => (
                  <tr key={r.cliente_id} data-testid={`row-${r.codice}`}>
                    <td style={{ paddingLeft: 18 }}>
                      <div style={{ fontWeight: 600, color: "var(--pa-text)" }}>
                        {r.nome}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--pa-muted)", marginTop: 2 }}>
                        {r.codice}
                      </div>
                    </td>
                    <td>
                      <span
                        className={
                          "pa-consulente-badge" +
                          (r.consulente_parser_status === "da_mappare" ? " warn" : "")
                        }
                      >
                        {r.consulente_parser_status === "da_mappare" && (
                          <AlertTriangle size={11} />
                        )}
                        {r.consulente_nome}
                      </span>
                    </td>
                    {r.months.map((cell, i) => {
                      const m = i + 1;
                      const key = `${r.codice}-${m}`;
                      const busy = busyCells.has(key);
                      return (
                        <td
                          key={i}
                          style={{
                            textAlign: "center",
                            paddingLeft: 2,
                            paddingRight: 2,
                            opacity: busy ? 0.5 : 1,
                          }}
                        >
                          <ContextMenu>
                            <ContextMenuTrigger asChild>
                              <div style={{ display: "inline-block" }}>
                                <MonthCell
                                  cell={cell}
                                  label={MESI_SHORT[i]}
                                  onClick={() => !busy && handleCellClick(r, m, cell)}
                                />
                              </div>
                            </ContextMenuTrigger>
                            <ContextMenuContent className="w-64">
                              <ContextMenuItem
                                onSelect={() =>
                                  nav(`/cliente/${r.codice}/${anno}/${m}`)
                                }
                                data-testid={`ctx-open-detail-${r.codice}-${m}`}
                              >
                                <ExternalLink size={13} style={{ marginRight: 8 }} />
                                Apri dettaglio (carica PDF)
                              </ContextMenuItem>
                              {(cell?.status === "vuoto" || !cell) && (
                                <ContextMenuItem
                                  onSelect={() =>
                                    toggleAcquisitoEsterno(r, m, true)
                                  }
                                  data-testid={`ctx-mark-acquisito-${r.codice}-${m}`}
                                >
                                  <Check size={13} style={{ marginRight: 8 }} />
                                  Segna come già acquisito
                                </ContextMenuItem>
                              )}
                              {cell?.status === "acquisito_esterno" && (
                                <ContextMenuItem
                                  onSelect={() =>
                                    toggleAcquisitoEsterno(r, m, false)
                                  }
                                  data-testid={`ctx-unmark-acquisito-${r.codice}-${m}`}
                                >
                                  <CircleSlash size={13} style={{ marginRight: 8 }} />
                                  Rimuovi spunta &quot;già acquisito&quot;
                                </ContextMenuItem>
                              )}
                            </ContextMenuContent>
                          </ContextMenu>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "right", paddingRight: 18 }}>
                      <BatchPopoverButton
                        row={r}
                        anno={anno}
                        onAfterGenerate={load}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 16,
            color: "var(--pa-muted)",
            fontSize: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <LegendItem cls="pa-cell-vuoto" text="—  niente (clic = apri, tasto destro = opzioni)" />
          <LegendItem cls="pa-cell-pdf" text="N  PDF caricati" />
          <LegendItem cls="pa-cell-ok" text="✓  Excel pronto" />
          <LegendItem cls="pa-cell-esterno" text="✓  Già acquisito (segnato manualmente)" />
          <LegendItem cls="pa-cell-errore" text="!  Errore" />
        </div>
      </div>

      <BulkMarkDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        clienti={data?.rows || []}
        defaultAnno={anno}
        onDone={(summary) => {
          setBulkDialogOpen(false);
          setToast({
            type: "ok",
            text: `Marcatura completata: ${summary.creati + summary.modificati} celle aggiornate${summary.saltati_con_pdf ? `, ${summary.saltati_con_pdf} saltate (hanno PDF)` : ""}.`,
          });
          setTimeout(() => setToast(null), 6000);
          load();
        }}
      />
    </div>
  );
}

function BatchPopoverButton({ row, anno, onAfterGenerate }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const selectableMonths = row.months
    .map((cell, i) => ({ cell, m: i + 1 }))
    .filter(({ cell }) => cell.status === "pdf_caricati" || cell.status === "excel_pronto");

  const reset = () => {
    setSelected(new Set());
    setResult(null);
    setErr("");
  };

  const toggle = (m) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
    setResult(null);
  };

  const selectAll = () => {
    setSelected(new Set(selectableMonths.map((x) => x.m)));
    setResult(null);
  };

  const generate = async () => {
    setBusy(true);
    setErr("");
    try {
      const periodi = Array.from(selected)
        .sort((a, b) => a - b)
        .map((m) => ({ anno, mese: m }));
      const r = await api.post("/batch-excel/generate", {
        cliente_codice: row.codice,
        periodi,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore.");
    } finally {
      setBusy(false);
    }
  };

  const noSelectable = selectableMonths.length === 0;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          // when the popover closes, propagate refresh to dashboard so any
          // newly created acquisizioni show up
          if (result) onAfterGenerate && onAfterGenerate();
          reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          data-testid={`open-batch-${row.codice}`}
          disabled={noSelectable}
          title={
            noSelectable
              ? "Nessun mese con PDF da combinare"
              : "Crea un Excel unico che unisce più mesi"
          }
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            border: "1px solid var(--pa-border)",
            background: noSelectable ? "transparent" : "white",
            color: noSelectable ? "var(--pa-muted)" : "var(--pa-navy)",
            cursor: noSelectable ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            opacity: noSelectable ? 0.55 : 1,
          }}
        >
          <Layers size={13} /> Excel multi-mese
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-80 bg-white border shadow-lg"
        style={{ padding: 14 }}
        data-testid={`batch-popover-${row.codice}`}
      >
        <div style={{ fontSize: 13, color: "var(--pa-muted)", marginBottom: 4 }}>
          Excel unico multi-mese
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--pa-navy)", marginBottom: 12 }}>
          {row.nome}
        </div>

        {err && (
          <div
            style={{
              background: "var(--pa-red-bg)",
              color: "var(--pa-red)",
              padding: "8px 10px",
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 10,
            }}
            data-testid={`batch-error-${row.codice}`}
          >
            {err}
          </div>
        )}

        {!result && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 6,
                marginBottom: 10,
              }}
            >
              {MESI_SHORT.map((label, i) => {
                const m = i + 1;
                const cell = row.months[i];
                const isSelectable =
                  cell.status === "pdf_caricati" || cell.status === "excel_pronto";
                const isChecked = selected.has(m);
                return (
                  <label
                    key={m}
                    title={
                      isSelectable
                        ? `${MESI_FULL[m]} ${anno}`
                        : "Nessun PDF in questo mese"
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 8px",
                      border: "1px solid var(--pa-border)",
                      borderRadius: 6,
                      cursor: isSelectable ? "pointer" : "not-allowed",
                      background: isChecked
                        ? "rgba(46, 190, 196, 0.12)"
                        : isSelectable
                          ? "white"
                          : "#F7F8FA",
                      borderColor: isChecked
                        ? "var(--pa-turquoise)"
                        : "var(--pa-border)",
                      fontSize: 13,
                      color: isSelectable ? "var(--pa-text)" : "var(--pa-muted)",
                      opacity: isSelectable ? 1 : 0.55,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(m)}
                      disabled={!isSelectable}
                      data-testid={`batch-check-${row.codice}-${m}`}
                      style={{ cursor: isSelectable ? "pointer" : "not-allowed" }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button
                onClick={selectAll}
                data-testid={`batch-selectall-${row.codice}`}
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  background: "transparent",
                  border: "1px solid var(--pa-border)",
                  borderRadius: 6,
                  color: "var(--pa-muted)",
                  cursor: "pointer",
                }}
              >
                Tutti i mesi con PDF
              </button>
              {selected.size > 0 && (
                <button
                  onClick={() => setSelected(new Set())}
                  style={{
                    fontSize: 12,
                    padding: "4px 10px",
                    background: "transparent",
                    border: "1px solid var(--pa-border)",
                    borderRadius: 6,
                    color: "var(--pa-muted)",
                    cursor: "pointer",
                  }}
                >
                  Pulisci
                </button>
              )}
            </div>

            <button
              onClick={generate}
              disabled={busy || selected.size === 0}
              className="pa-btn-accent"
              data-testid={`batch-generate-${row.codice}`}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                fontWeight: 600,
                border: "none",
                cursor: busy || selected.size === 0 ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                opacity: busy || selected.size === 0 ? 0.55 : 1,
              }}
            >
              {busy ? <span className="pa-spinner" /> : <FileSpreadsheet size={14} />}
              {busy
                ? "Generazione…"
                : `Crea Excel${
                    selected.size > 0 ? ` (${selected.size} mes${selected.size === 1 ? "e" : "i"})` : ""
                  }`}
            </button>
          </>
        )}

        {result && (
          <div data-testid={`batch-result-${row.codice}`}>
            <div
              style={{
                background: "var(--pa-green-bg)",
                color: "var(--pa-green)",
                padding: "10px 12px",
                borderRadius: 8,
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              ✓ Excel generato — {result.n_righe_contabili} righe contabili,
              sbilancio{" "}
              {Math.abs(result.sbilancio) < 0.01
                ? "+0,00 (quadrato)"
                : (result.sbilancio >= 0 ? "+" : "") + Number(result.sbilancio).toFixed(2)}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--pa-muted)",
                marginBottom: 10,
                wordBreak: "break-all",
              }}
            >
              {result.filename}
            </div>
            {(() => {
              const token = localStorage.getItem("pa_token") || "";
              const url = `${API}/batch-excel/${result.id}/download?token=${encodeURIComponent(token)}`;
              const openNewTab = (e) => {
                e.preventDefault();
                window.open(url, "_blank", "noopener");
              };
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={result.filename}
                  onClick={openNewTab}
                  className="pa-btn-primary"
                  data-testid={`batch-download-${row.codice}`}
                  style={{
                    display: "inline-flex",
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontWeight: 600,
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    textDecoration: "none",
                  }}
                >
                  <Download size={14} /> Scarica Excel
                </a>
              );
            })()}
            <button
              onClick={reset}
              style={{
                marginTop: 8,
                width: "100%",
                padding: "6px 10px",
                background: "transparent",
                border: "1px solid var(--pa-border)",
                borderRadius: 6,
                color: "var(--pa-muted)",
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Nuova selezione
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function BulkMarkDialog({ open, onOpenChange, clienti, defaultAnno, onDone }) {
  const [anno, setAnno] = useState(defaultAnno || 2026);
  const [meseFrom, setMeseFrom] = useState(1);
  const [meseTo, setMeseTo] = useState(12);
  const [tuttiClienti, setTuttiClienti] = useState(true);
  const [selezione, setSelezione] = useState(new Set());
  const [flagged, setFlagged] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAnno(defaultAnno || 2026);
      setMeseFrom(1);
      setMeseTo(12);
      setTuttiClienti(true);
      setSelezione(new Set());
      setFlagged(true);
      setOverwrite(false);
      setError("");
    }
  }, [open, defaultAnno]);

  const anniOpts = useMemo(() => {
    const y = new Date().getFullYear();
    const arr = new Set([2026, y]);
    for (let i = y - 5; i <= y + 1; i++) arr.add(i);
    return Array.from(arr).sort((a, b) => b - a);
  }, []);

  const toggleCliente = (codice) => {
    setSelezione((prev) => {
      const next = new Set(prev);
      if (next.has(codice)) next.delete(codice);
      else next.add(codice);
      return next;
    });
  };

  const numCelleStimate = (() => {
    const numClienti = tuttiClienti ? clienti.length : selezione.size;
    const numMesi = Math.max(0, meseTo - meseFrom + 1);
    return numClienti * numMesi;
  })();

  const apply = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {
        anno,
        mese_from: meseFrom,
        mese_to: meseTo,
        cliente_codici: tuttiClienti ? null : Array.from(selezione),
        flagged,
        overwrite_with_pdf: overwrite,
      };
      const r = await api.post("/acquisizioni/bulk-mark-external", payload);
      onDone && onDone(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nell'operazione.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="bulk-mark-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: "var(--pa-navy)" }}>
            {flagged ? "Marca periodi come acquisiti ✓" : "Rimuovi marca acquisito"}
          </DialogTitle>
          <DialogDescription>
            Imposta in massa lo stato di più mesi e più clienti contemporaneamente.
            Le celle che hanno PDF caricati o Excel pronto vengono saltate per default.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
          {/* Toggle marca / smarca */}
          <div style={{ display: "flex", gap: 8 }}>
            <ToggleButton
              active={flagged}
              onClick={() => setFlagged(true)}
              testid="bulk-mode-flag"
            >
              ✓ Marca come acquisiti
            </ToggleButton>
            <ToggleButton
              active={!flagged}
              onClick={() => setFlagged(false)}
              testid="bulk-mode-unflag"
            >
              Annulla marca
            </ToggleButton>
          </div>

          {/* Periodo */}
          <div>
            <Label>Periodo</Label>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={anno}
                onChange={(e) => setAnno(Number(e.target.value))}
                data-testid="bulk-anno"
                style={selectStyle}
              >
                {anniOpts.map((y) => (
                  <option key={y} value={y}>
                    Anno {y}
                  </option>
                ))}
              </select>
              <span style={{ color: "var(--pa-muted)", fontSize: 13 }}>da</span>
              <select
                value={meseFrom}
                onChange={(e) => setMeseFrom(Number(e.target.value))}
                data-testid="bulk-mese-from"
                style={selectStyle}
              >
                {MESI_FULL.slice(1).map((m, i) => (
                  <option key={i + 1} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
              <span style={{ color: "var(--pa-muted)", fontSize: 13 }}>a</span>
              <select
                value={meseTo}
                onChange={(e) => setMeseTo(Number(e.target.value))}
                data-testid="bulk-mese-to"
                style={selectStyle}
              >
                {MESI_FULL.slice(1).map((m, i) => (
                  <option key={i + 1} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            {meseFrom > meseTo && (
              <div style={{ color: "var(--pa-red)", fontSize: 12, marginTop: 6 }}>
                Il mese "da" deve essere ≤ "a".
              </div>
            )}
          </div>

          {/* Clienti */}
          <div>
            <Label>Clienti</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <ToggleButton
                active={tuttiClienti}
                onClick={() => setTuttiClienti(true)}
                testid="bulk-all-clients"
              >
                Tutti i {clienti.length} clienti
              </ToggleButton>
              <ToggleButton
                active={!tuttiClienti}
                onClick={() => setTuttiClienti(false)}
                testid="bulk-select-clients"
              >
                Seleziona ({selezione.size})
              </ToggleButton>
            </div>
            {!tuttiClienti && (
              <div
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  border: "1px solid var(--pa-border)",
                  borderRadius: 8,
                  padding: 8,
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 4,
                }}
                data-testid="bulk-clients-list"
              >
                {clienti.map((c) => (
                  <label
                    key={c.codice}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 8px",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selezione.has(c.codice)}
                      onChange={() => toggleCliente(c.codice)}
                      data-testid={`bulk-cli-${c.codice}`}
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.nome}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Override option */}
          {flagged && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--pa-muted)",
                padding: "8px 10px",
                background: "#F7F8FA",
                borderRadius: 6,
              }}
            >
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                data-testid="bulk-overwrite"
              />
              Sovrascrivi anche i mesi che hanno PDF / Excel pronto (sconsigliato)
            </label>
          )}

          {/* Anteprima */}
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "rgba(46, 190, 196, 0.08)",
              border: "1px solid rgba(46, 190, 196, 0.25)",
              fontSize: 13,
              color: "var(--pa-navy)",
            }}
            data-testid="bulk-preview"
          >
            <strong>Anteprima:</strong> {flagged ? "verranno marcati" : "verranno smarcati"}{" "}
            fino a <strong>{numCelleStimate}</strong> celle (
            {tuttiClienti ? clienti.length : selezione.size} client
            {(tuttiClienti ? clienti.length : selezione.size) === 1 ? "e" : "i"} ×{" "}
            {Math.max(0, meseTo - meseFrom + 1)} mes
            {Math.max(0, meseTo - meseFrom + 1) === 1 ? "e" : "i"} del {anno}).
          </div>

          {error && (
            <div
              style={{
                background: "var(--pa-red-bg)",
                color: "var(--pa-red)",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 13,
              }}
              data-testid="bulk-error"
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="bulk-cancel"
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              fontWeight: 600,
              cursor: "pointer",
              background: "transparent",
              border: "1px solid var(--pa-border)",
              color: "var(--pa-muted)",
            }}
          >
            Annulla
          </button>
          <button
            onClick={apply}
            disabled={
              busy ||
              meseFrom > meseTo ||
              (!tuttiClienti && selezione.size === 0)
            }
            className="pa-btn-accent"
            data-testid="bulk-apply"
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              fontWeight: 600,
              border: "none",
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              opacity:
                meseFrom > meseTo || (!tuttiClienti && selezione.size === 0)
                  ? 0.5
                  : 1,
            }}
          >
            {busy ? <span className="pa-spinner" /> : <CheckSquare size={14} />}
            {busy ? "Applicazione…" : "Applica"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        padding: "7px 14px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        border: active ? "1px solid var(--pa-turquoise)" : "1px solid var(--pa-border)",
        background: active ? "rgba(46, 190, 196, 0.13)" : "white",
        color: active ? "var(--pa-navy)" : "var(--pa-muted)",
      }}
    >
      {children}
    </button>
  );
}

function Label({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "var(--pa-muted)",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

const selectStyle = {
  padding: "7px 10px",
  border: "1px solid var(--pa-border)",
  borderRadius: 6,
  fontSize: 13,
  background: "white",
  cursor: "pointer",
};

function LegendItem({ cls, text }) {  let symbol = "—";
  if (cls === "pa-cell-ok" || cls === "pa-cell-esterno") symbol = "✓";
  else if (cls === "pa-cell-pdf") symbol = "N";
  else if (cls === "pa-cell-errore") symbol = "!";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span className={`pa-cell ${cls}`} style={{ width: 26, height: 22, fontSize: 11, cursor: "default" }}>
        {symbol}
      </span>
      {text}
    </span>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        color: "var(--pa-text)",
      }}
    >
      <span style={{ color: "var(--pa-muted)", fontWeight: 500 }}>{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        style={{
          padding: "6px 10px",
          border: "1px solid var(--pa-border)",
          borderRadius: 6,
          fontSize: 13,
          background: "white",
          cursor: "pointer",
          minWidth: 120,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
